import React from "react";
import { Text, Card, Flex } from "@jsq/ds/core";
import { Sizes } from "@jsq/ds/styles";
import { Money } from "~/components/Money";
import { type ProcessingPaymentsWithConfirmationStatusQuery } from "~/graphql/supergraphTypes";
import { toDbId } from "~/utils/graphql-helpers";
import { FieldRow } from "../../utils/FieldRow";
import { formatFieldValue } from "../../utils/formatFieldValue";
import FidesProviderStatus from "./FidesProviderStatus";
import ModernTreasuryProviderStatus from "./ModernTreasuryProviderStatus";
import * as styles from "./styles_vanilla.css";

type ProcessingPaymentWithProviderStatusType =
  ProcessingPaymentsWithConfirmationStatusQuery["processingPaymentsWithConfirmationStatus"][0]["payments"][0];

type ProviderStatusType = NonNullable<
  ProcessingPaymentWithProviderStatusType["adminProviderStatus"]
>;

type FidesProviderStatusType = Extract<
  ProviderStatusType,
  { __typename?: "FidesProviderStatusDto" }
>;

interface ProcessingPaymentCardProps {
  paymentData: ProcessingPaymentWithProviderStatusType;
  paymentIndex: number;
}

const isFidesProviderStatus = (
  status: ProviderStatusType | null | undefined,
): status is FidesProviderStatusType => {
  return status?.__typename === "FidesProviderStatusDto";
};

const isModernTreasuryProviderStatus = (
  status: ProviderStatusType | null | undefined,
): status is Extract<
  ProviderStatusType,
  { __typename?: "ModernTreasuryProviderStatusDto" }
> => {
  return status?.__typename === "ModernTreasuryProviderStatusDto";
};

const ProcessingPaymentCard: React.FC<ProcessingPaymentCardProps> = ({
  paymentData,
  paymentIndex,
}) => {
  const adminProviderStatus = paymentData.adminProviderStatus;

  return (
    <Card className={styles.cardWrapper}>
      <Flex flexDirection="column" gap={Sizes.SM} className={styles.cardContent}>
        <Text variant="strong-md">Payment #{paymentIndex}</Text>

        <FieldRow label="ID:" value={formatFieldValue(paymentData.id)} labelMinWidth="180px" />
        <FieldRow
          label="Status:"
          value={formatFieldValue(paymentData.status)}
          labelMinWidth="180px"
        />
        <FieldRow
          label="Amount:"
          value={<Money money={paymentData.amount} rounded={false} />}
          labelMinWidth="180px"
        />
        <FieldRow
          label="Object Type:"
          value={formatFieldValue(paymentData.objectType)}
          labelMinWidth="180px"
        />
        <FieldRow
          label="Object ID:"
          value={formatFieldValue(toDbId(paymentData.objectId))}
          labelMinWidth="180px"
        />
        <FieldRow
          label="Reference Number:"
          value={formatFieldValue(paymentData.referenceNumber)}
          labelMinWidth="180px"
        />
        <FieldRow
          label="Direction:"
          value={formatFieldValue(paymentData.direction)}
          labelMinWidth="180px"
        />
        <FieldRow
          label="Method:"
          value={formatFieldValue(paymentData.method)}
          labelMinWidth="180px"
        />
        <FieldRow
          label="Transmitted At:"
          value={formatFieldValue(paymentData.transmittedAt)}
          labelMinWidth="180px"
        />
        <FieldRow
          label="Settled At:"
          value={formatFieldValue(paymentData.settledAt)}
          labelMinWidth="180px"
        />
        <FieldRow
          label="Failed At:"
          value={formatFieldValue(paymentData.failedAt)}
          labelMinWidth="180px"
        />
        <FieldRow
          label="Scheduled Process Date:"
          value={formatFieldValue(paymentData.scheduledProcessDate)}
          labelMinWidth="180px"
        />
        <FieldRow
          label="Failure Reason:"
          value={formatFieldValue(paymentData.failureReason)}
          labelMinWidth="180px"
        />
        <FieldRow
          label="Return Code:"
          value={formatFieldValue(paymentData.returnCode)}
          labelMinWidth="180px"
        />
        <FieldRow
          label="Return Reason:"
          value={formatFieldValue(paymentData.returnReason)}
          labelMinWidth="180px"
        />

        {isFidesProviderStatus(adminProviderStatus) && (
          <FidesProviderStatus status={adminProviderStatus} />
        )}
        {isModernTreasuryProviderStatus(adminProviderStatus) && (
          <ModernTreasuryProviderStatus />
        )}
      </Flex>
    </Card>
  );
};

export default ProcessingPaymentCard;
export { ProcessingPaymentCard };
export type { ProcessingPaymentCardProps };
